/**
 * Prestations PostDev — préfixe URL + ville (ex. /referencement-seo-lille).
 * Source unique pour routes, sitemap et maillage interne.
 */
export interface LocalService {
  id: string
  /** Segment URL avant le slug ville : /{urlPrefix}-{ville} */
  urlPrefix: string
  label: string
  shortDescription: string
  icon: string
  category: 'agency' | 'web' | 'mobile' | 'software' | 'marketing' | 'infra' | 'consulting'
}

/** Page hub ville : /local/{slug} (ex. /local/grande-synthe) */
export const LOCAL_HUB_SERVICE_ID = 'solutions-digitales'

/** Index des communes couvertes */
export const LOCAL_ZONES_INDEX_PATH = '/local'

export function localCityHubPath(citySlug: string): string {
  return `/local/${citySlug}`
}

export const LOCAL_SERVICES: LocalService[] = [
  {
    id: 'agence-web',
    urlPrefix: 'agence-web',
    label: 'Agence web et solutions digitales',
    shortDescription:
      'Accompagnement global : stratégie digitale, conception et développement pour les entreprises de la région.',
    icon: 'fas fa-building',
    category: 'agency'
  },
  {
    id: 'creation-site-vitrine',
    urlPrefix: 'creation-site-vitrine',
    label: 'Création de site vitrine',
    shortDescription:
      'Sites vitrines professionnels, rapides et optimisés pour présenter votre activité et générer des contacts.',
    icon: 'fas fa-window-maximize',
    category: 'web'
  },
  {
    id: 'creation-site-dynamique',
    urlPrefix: 'creation-site-dynamique',
    label: 'Création de site dynamique',
    shortDescription:
      'Sites interactifs sur mesure : espaces clients, formulaires avancés, tableaux de bord et contenus évolutifs.',
    icon: 'fas fa-sync-alt',
    category: 'web'
  },
  {
    id: 'application-web',
    urlPrefix: 'application-web',
    label: 'Application web',
    shortDescription:
      'Applications web métier accessibles depuis le navigateur : SaaS, outils internes et plateformes collaboratives.',
    icon: 'fas fa-laptop-code',
    category: 'web'
  },
  {
    id: 'application-mobile',
    urlPrefix: 'application-mobile',
    label: 'Application mobile iOS et Android',
    shortDescription:
      'Applications natives et cross-platform pour iPhone, iPad et Android, avec publication sur les stores.',
    icon: 'fas fa-mobile-alt',
    category: 'mobile'
  },
  {
    id: 'logiciel-sur-mesure',
    urlPrefix: 'logiciel-sur-mesure',
    label: 'Logiciel sur mesure',
    shortDescription:
      'Logiciels métiers, automatisation et intégrations API adaptés à vos processus (CRM, ERP, outils internes).',
    icon: 'fas fa-cogs',
    category: 'software'
  },
  {
    id: 'referencement-seo',
    urlPrefix: 'referencement-seo',
    label: 'Référencement naturel (SEO)',
    shortDescription:
      'Audit technique, optimisation des contenus, maillage interne et visibilité Google pour votre site.',
    icon: 'fas fa-chart-line',
    category: 'marketing'
  },
  {
    id: 'referencement-sea',
    urlPrefix: 'referencement-sea',
    label: 'Référencement payant (SEA)',
    shortDescription:
      'Campagnes Google Ads et publicité en ligne pour un trafic qualifié et des conversions mesurables.',
    icon: 'fas fa-ad',
    category: 'marketing'
  },
  {
    id: 'optimisation-geo',
    urlPrefix: 'optimisation-geo',
    label: 'Optimisation GEO (moteurs IA)',
    shortDescription:
      'Visibilité sur ChatGPT, Perplexity et Gemini : contenus structurés, autorité et citations de marque.',
    icon: 'fas fa-robot',
    category: 'marketing'
  },
  {
    id: 'hebergement-web',
    urlPrefix: 'hebergement-web',
    label: 'Hébergement web',
    shortDescription:
      'Hébergement sécurisé et performant pour vos sites et applications, avec monitoring et mises à jour.',
    icon: 'fas fa-server',
    category: 'infra'
  },
  {
    id: 'sauvegarde-donnees',
    urlPrefix: 'sauvegarde-donnees',
    label: 'Sauvegarde et backup',
    shortDescription:
      'Sauvegardes automatiques, plan de reprise 3-2-1 et restauration testée pour protéger vos données.',
    icon: 'fas fa-database',
    category: 'infra'
  },
  {
    id: 'securite-web',
    urlPrefix: 'securite-web',
    label: 'Sécurité web',
    shortDescription:
      'SSL, durcissement serveur, correctifs, protection des accès et bonnes pratiques cybersécurité.',
    icon: 'fas fa-shield-alt',
    category: 'infra'
  },
  {
    id: 'audit-digital',
    urlPrefix: 'audit-digital',
    label: 'Audit digital',
    shortDescription:
      'Audit de site, performance, SEO technique, sécurité et parcours utilisateur avec plan d’action priorisé.',
    icon: 'fas fa-search',
    category: 'infra'
  },
  {
    id: 'solutions-cloud',
    urlPrefix: 'solutions-cloud',
    label: 'Solutions cloud',
    shortDescription:
      'Migration cloud, architecture scalable, DevOps et infogérance pour des applications fiables.',
    icon: 'fas fa-cloud',
    category: 'infra'
  },
  // —— Web & e-commerce ——
  {
    id: 'creation-site-ecommerce',
    urlPrefix: 'creation-site-ecommerce',
    label: 'Création de site e-commerce',
    shortDescription:
      'Boutiques en ligne sur mesure : catalogue, paiement sécurisé, gestion des commandes et performance.',
    icon: 'fas fa-shopping-cart',
    category: 'web'
  },
  {
    id: 'refonte-site-web',
    urlPrefix: 'refonte-site-web',
    label: 'Refonte de site web',
    shortDescription:
      'Modernisation de votre site sans perdre le SEO : design, technique, contenus et mise en production.',
    icon: 'fas fa-redo-alt',
    category: 'web'
  },
  {
    id: 'maintenance-site-web',
    urlPrefix: 'maintenance-site-web',
    label: 'Maintenance et support web',
    shortDescription:
      'Forfaits de maintenance : mises à jour, correctifs, évolutions et support réactif pour votre site ou app.',
    icon: 'fas fa-tools',
    category: 'web'
  },
  {
    id: 'developpement-wordpress',
    urlPrefix: 'developpement-wordpress',
    label: 'Développement WordPress sur mesure',
    shortDescription:
      'Sites WordPress professionnels : thème sur mesure, performances, sécurité et plugins adaptés à votre métier.',
    icon: 'fab fa-wordpress',
    category: 'web'
  },
  {
    id: 'conception-ux-ui',
    urlPrefix: 'conception-ux-ui',
    label: 'Conception UX/UI et maquettes',
    shortDescription:
      'Parcours utilisateur, wireframes et maquettes Figma pour des interfaces claires et convertissantes.',
    icon: 'fas fa-pencil-ruler',
    category: 'web'
  },
  {
    id: 'accessibilite-site-web',
    urlPrefix: 'accessibilite-site-web',
    label: 'Accessibilité web (RGAA)',
    shortDescription:
      'Mise en conformité RGAA : audit, corrections et bonnes pratiques pour un site accessible à tous.',
    icon: 'fas fa-universal-access',
    category: 'web'
  },
  {
    id: 'optimisation-performance-web',
    urlPrefix: 'optimisation-performance-web',
    label: 'Optimisation performance web',
    shortDescription:
      'Core Web Vitals, temps de chargement et optimisation front/back pour un site rapide et bien référencé.',
    icon: 'fas fa-tachometer-alt',
    category: 'web'
  },
  // —— Produit & logiciel ——
  {
    id: 'application-pwa',
    urlPrefix: 'application-pwa',
    label: 'Application PWA',
    shortDescription:
      'Progressive Web App : expérience mobile depuis le navigateur, hors-ligne et installable sans store.',
    icon: 'fas fa-tablet-alt',
    category: 'mobile'
  },
  {
    id: 'mvp-startup',
    urlPrefix: 'mvp-startup',
    label: 'MVP et prototype startup',
    shortDescription:
      'Minimum viable product en quelques semaines : cadrage agile, développement itératif et mise en production.',
    icon: 'fas fa-rocket',
    category: 'software'
  },
  {
    id: 'integration-api-crm',
    urlPrefix: 'integration-api-crm',
    label: 'Intégration API, CRM et ERP',
    shortDescription:
      'Connexion de vos outils : synchronisation données, APIs REST, webhooks et flux métiers automatisés.',
    icon: 'fas fa-plug',
    category: 'software'
  },
  {
    id: 'automatisation-metier',
    urlPrefix: 'automatisation-metier',
    label: 'Automatisation métier',
    shortDescription:
      'Scripts, workflows et outils pour automatiser les tâches répétitives et gagner en productivité.',
    icon: 'fas fa-magic',
    category: 'software'
  },
  {
    id: 'tableau-de-bord-metier',
    urlPrefix: 'tableau-de-bord-metier',
    label: 'Tableau de bord et BI',
    shortDescription:
      'Dashboards sur mesure : indicateurs métier, visualisation de données et aide à la décision.',
    icon: 'fas fa-chart-pie',
    category: 'software'
  },
  {
    id: 'chatbot-ia-entreprise',
    urlPrefix: 'chatbot-ia-entreprise',
    label: 'Chatbot et assistant IA',
    shortDescription:
      'Assistants conversationnels et IA métier intégrés à votre site ou outils internes (RAG, support, FAQ).',
    icon: 'fas fa-comments',
    category: 'software'
  },
  // —— Infra, sécurité, conformité ——
  {
    id: 'migration-site-hebergement',
    urlPrefix: 'migration-site-hebergement',
    label: 'Migration de site et hébergement',
    shortDescription:
      'Transfert sécurisé de site, emails et bases : planification, bascule sans coupure et tests post-migration.',
    icon: 'fas fa-exchange-alt',
    category: 'infra'
  },
  {
    id: 'rgpd-site-web',
    urlPrefix: 'rgpd-site-web',
    label: 'Conformité RGPD technique',
    shortDescription:
      'Cookies, consentement, politique de confidentialité et traitements : mise en conformité côté site et données.',
    icon: 'fas fa-user-shield',
    category: 'infra'
  },
  {
    id: 'monitoring-site-web',
    urlPrefix: 'monitoring-site-web',
    label: 'Monitoring et supervision',
    shortDescription:
      'Surveillance disponibilité, alertes et tableaux de bord pour anticiper les pannes de vos services web.',
    icon: 'fas fa-heartbeat',
    category: 'infra'
  },
  {
    id: 'plan-reprise-activite-web',
    urlPrefix: 'plan-reprise-activite-web',
    label: 'Plan de reprise d\'activité (PRA)',
    shortDescription:
      'Procédures et tests de restauration pour garantir la continuité de votre activité en cas d\'incident.',
    icon: 'fas fa-life-ring',
    category: 'infra'
  },
  // —— Marketing digital ——
  {
    id: 'publicite-reseaux-sociaux',
    urlPrefix: 'publicite-reseaux-sociaux',
    label: 'Publicité réseaux sociaux',
    shortDescription:
      'Campagnes Meta Ads, LinkedIn et social selling : ciblage, créas et optimisation du coût par lead.',
    icon: 'fas fa-share-alt',
    category: 'marketing'
  },
  {
    id: 'analytics-ga4',
    urlPrefix: 'analytics-ga4',
    label: 'Analytics et GA4',
    shortDescription:
      'Google Analytics 4, balises, conversions et tableaux de bord pour piloter votre performance digitale.',
    icon: 'fas fa-chart-bar',
    category: 'marketing'
  },
  {
    id: 'redaction-web-seo',
    urlPrefix: 'redaction-web-seo',
    label: 'Rédaction web et content SEO',
    shortDescription:
      'Articles, pages de vente et contenus optimisés pour le référencement et la conversion.',
    icon: 'fas fa-pen-fancy',
    category: 'marketing'
  },
  {
    id: 'emailing-automation',
    urlPrefix: 'emailing-automation',
    label: 'Emailing et automation marketing',
    shortDescription:
      'Newsletters, scénarios automation (Brevo, etc.) et nurturing pour fidéliser et convertir vos prospects.',
    icon: 'fas fa-envelope-open-text',
    category: 'marketing'
  },
  // —— Conseil ——
  {
    id: 'conseil-transformation-digitale',
    urlPrefix: 'conseil-transformation-digitale',
    label: 'Conseil transformation digitale',
    shortDescription:
      'Accompagnement stratégique des dirigeants : audit maturité digitale, feuille de route et priorisation des projets.',
    icon: 'fas fa-compass',
    category: 'consulting'
  },
  {
    id: 'cahier-des-charges-web',
    urlPrefix: 'cahier-des-charges-web',
    label: 'Cahier des charges web',
    shortDescription:
      'Spécifications fonctionnelles et techniques pour cadrer votre projet avant développement ou appel d\'offres.',
    icon: 'fas fa-file-alt',
    category: 'consulting'
  },
  {
    id: 'conseil-stack-technique',
    urlPrefix: 'conseil-stack-technique',
    label: 'Conseil stack technique',
    shortDescription:
      'Choix des technologies (front, back, cloud, mobile) adaptés à votre budget, délais et évolutivité.',
    icon: 'fas fa-code-branch',
    category: 'consulting'
  }
]

const byId = new Map(LOCAL_SERVICES.map((s) => [s.id, s]))
const byPrefix = new Map(LOCAL_SERVICES.map((s) => [s.urlPrefix, s]))

/** Préfixes du plus long au plus court pour le parsing d’URL */
const PREFIXES_LONGEST_FIRST = [...LOCAL_SERVICES]
  .map((s) => s.urlPrefix)
  .sort((a, b) => b.length - a.length)

export function getLocalServiceById(id: string): LocalService | undefined {
  if (id === LOCAL_HUB_SERVICE_ID) {
    return {
      id: LOCAL_HUB_SERVICE_ID,
      urlPrefix: 'hub',
      label: 'Solutions digitales',
      shortDescription:
        'Sites web, applications mobiles, logiciels, SEO, hébergement et cybersécurité pour les entreprises des Hauts-de-France.',
      icon: 'fas fa-layer-group',
      category: 'agency'
    }
  }
  return byId.get(id)
}

export function getLocalServiceByPrefix(prefix: string): LocalService | undefined {
  return byPrefix.get(prefix)
}

export function serviceCityPath(service: LocalService, citySlug: string): string {
  if (service.id === LOCAL_HUB_SERVICE_ID) {
    return localCityHubPath(citySlug)
  }
  return `/${service.urlPrefix}-${citySlug}`
}

/**
 * Extrait service + ville depuis un chemin (/referencement-seo-lille).
 */
export function parseServiceCityPath(
  path: string
): { service: LocalService; citySlug: string } | null {
  const clean = path.replace(/^\/+|\/+$/g, '')
  if (!clean) return null

  for (const prefix of PREFIXES_LONGEST_FIRST) {
    const needle = `${prefix}-`
    if (!clean.startsWith(needle)) continue
    const citySlug = clean.slice(needle.length)
    if (!citySlug || citySlug.includes('/')) continue
    const service = byPrefix.get(prefix)
    if (service) return { service, citySlug }
  }

  if (clean.startsWith('local/')) {
    const citySlug = clean.slice('local/'.length)
    if (citySlug && !citySlug.includes('/')) {
      const hub = getLocalServiceById(LOCAL_HUB_SERVICE_ID)!
      return { service: hub, citySlug }
    }
  }

  return null
}

export function buildCommuneUrls(citySlug: string): Record<string, string> {
  const urls: Record<string, string> = {
    local: localCityHubPath(citySlug)
  }
  for (const svc of LOCAL_SERVICES) {
    urls[svc.id] = serviceCityPath(svc, citySlug)
  }
  return urls
}
